import React, { useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { navigationUiStateResource } from '@atlassian/jira-router-resources-navigation-ui-state/src/index.tsx';
import { useResource, createRouterSelector } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createContainer } from '@atlassian/react-sweet-state';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { navigationUIStateRelayResource } from '@atlassian/jira-navigation-apps-resources/src/services/navigation-state/index.tsx';
import type { RightSidebarState, LayoutState } from '../../../../common/types.tsx';
import { actions, type LayoutActions } from '../../actions/index.tsx';
import { layoutStore, DEFAULT_LEFT_SIDEBAR_WIDTH } from '../../store/index.tsx';
import type { layoutManagerQuery } from './__generated__/layoutManagerQuery.graphql';
import type { LayoutManagerProps, LayoutContainerProps } from './types.tsx';

const LayoutContainer = createContainer<LayoutState, LayoutActions, LayoutContainerProps>(
	layoutStore,
	{
		displayName: 'JiraLayoutContainer',
		onInit:
			() =>
			({ dispatch }, { leftSidebar, rightSidebar, isChromeless }) => {
				dispatch(actions.setIsChromeless(isChromeless));
				dispatch(actions.setLeftSidebarState(leftSidebar));
				dispatch(actions.setRightSidebarState(rightSidebar));
			},
		onUpdate:
			() =>
			({ dispatch }, { isChromeless }) => {
				dispatch(actions.setIsChromeless(isChromeless));
			},
	},
);

const DEFFAULT_RIGHT_SIDEBAR_STATE: RightSidebarState = {
	stackState: [],
};

const useIsChromeless = createRouterSelector(
	(state) =>
		state.route.layout?.isChromeless === true ||
		state.route.entryPoint?.()?.toString() === 'ChromelessEntryPoint',
);

export const LayoutManager = componentWithCondition(
	() => fg('blu-8363-archive-legacy-resource-navigationuistate'),
	LayoutManagerNew,
	LayoutManagerOld,
);

function LayoutManagerNew({ children }: LayoutManagerProps) {
	const { queryReference } = useRelayResource(navigationUIStateRelayResource);

	return queryReference ? (
		<LayoutManagerNewWithQuery queryReference={queryReference}>
			{children}
		</LayoutManagerNewWithQuery>
	) : (
		<LayoutManagerNewContent>{children}</LayoutManagerNewContent>
	);
}

function LayoutManagerNewWithQuery({
	children,
	queryReference,
}: LayoutManagerProps & { queryReference: PreloadedQuery<layoutManagerQuery> }) {
	const data = usePreloadedQuery(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query layoutManagerQuery($cloudId: ID!) {
				jira {
					navigationUIState(cloudId: $cloudId) {
						isLeftSidebarCollapsed
						leftSidebarWidth
					}
				}
			}
		`,
		queryReference,
	);

	return (
		<LayoutManagerNewContent
			isLeftSidebarCollapsed={data?.jira?.navigationUIState?.isLeftSidebarCollapsed}
			leftSidebarWidth={data?.jira?.navigationUIState?.leftSidebarWidth}
		>
			{children}
		</LayoutManagerNewContent>
	);
}

function LayoutManagerNewContent({
	children,
	isLeftSidebarCollapsed,
	leftSidebarWidth,
}: {
	children: React.ReactNode;
	isLeftSidebarCollapsed?: boolean | null;
	leftSidebarWidth?: number | null;
}) {
	const isChromeless = useIsChromeless();

	const leftSidebar = useMemo(
		() => ({
			isVisible: true,
			isCollapsed: isLeftSidebarCollapsed ?? false,
			width: leftSidebarWidth ?? DEFAULT_LEFT_SIDEBAR_WIDTH,
			isFlyoutOpen: false,
		}),
		[isLeftSidebarCollapsed, leftSidebarWidth],
	);

	return (
		<LayoutContainer
			isGlobal
			isChromeless={isChromeless}
			leftSidebar={leftSidebar}
			rightSidebar={DEFFAULT_RIGHT_SIDEBAR_STATE}
		>
			{children}
		</LayoutContainer>
	);
}

function LayoutManagerOld({ children }: LayoutManagerProps) {
	const { data } = useResource(navigationUiStateResource);
	const isChromeless = useIsChromeless();

	const isLeftSidebarCollapsed = data?.leftSidebar?.isCollapsed ?? false;
	const leftSidebarWidth = data?.leftSidebar?.width ?? DEFAULT_LEFT_SIDEBAR_WIDTH;
	const leftSidebar = useMemo(
		() => ({
			isVisible: true,
			isCollapsed: isLeftSidebarCollapsed,
			width: leftSidebarWidth,
			isFlyoutOpen: false,
		}),
		[isLeftSidebarCollapsed, leftSidebarWidth],
	);

	return (
		<LayoutContainer
			isGlobal
			isChromeless={isChromeless}
			leftSidebar={leftSidebar}
			rightSidebar={DEFFAULT_RIGHT_SIDEBAR_STATE}
		>
			{children}
		</LayoutContainer>
	);
}
